// Plain copy for the MCP server landing page — no icons, no "use client", so it is
// safe to import from server code (the /api/md/tools/mcp-server route) as well as
// the client section.

import { DOCS, MCP_SERVERS_DIRECTORY_URL } from "@/lib/constants";

export interface ToolCopy {
  name: string;
  description: string;
}

export interface ToolCategoryCopy {
  slug: string;
  title: string;
  subtitle: string;
  tools: ToolCopy[];
}

export const MCP_TOOL_CATEGORIES_COPY: ToolCategoryCopy[] = [
  {
    slug: "account",
    title: "Account",
    subtitle: "Always loaded",
    tools: [
      {
        name: "get_subscription",
        description: "List active subscriptions, optionally filtered to one plan type",
      },
      {
        name: "get_outbound_ip",
        description: "Get the public IP of the machine running this server, for IP whitelisting",
      },
    ],
  },
  {
    slug: "per-proxy",
    title: "Per Proxy",
    subtitle: "14 tools",
    tools: [
      {
        name: "list_proxies",
        description: "List all proxies with IPs, connection URLs, speeds, usage, and tags",
      },
      { name: "get_proxy", description: "Get details of a single proxy by session ID" },
      {
        name: "rotate_proxy",
        description: "Rotate a proxy's IP, optionally locked to a city or carrier",
      },
      { name: "rotate_proxy_url", description: "Rotate a proxy using its rotation token" },
      {
        name: "set_rotation_interval",
        description: "Set auto-rotation from 60 seconds to 7 days, or disable it",
      },
      { name: "update_proxy_password", description: "Change the password for all proxies" },
      {
        name: "list_proxy_locations",
        description: "List available U.S. cities with average speeds",
      },
      {
        name: "list_proxy_carriers",
        description: "List available carriers, filterable by location",
      },
      { name: "create_ip_whitelist", description: "Whitelist source IPs for proxy access" },
      { name: "list_tags", description: "List all proxy tags" },
      { name: "create_tag", description: "Create a new tag" },
      { name: "delete_tag", description: "Delete a tag and unassign it from every proxy" },
      { name: "tag_proxy", description: "Assign a tag to a proxy session" },
      { name: "untag_proxy", description: "Remove a tag from a proxy session" },
    ],
  },
  {
    slug: "mobile",
    title: "Per GB Mobile",
    subtitle: "9 tools · mobile_ prefix",
    tools: [
      { name: "mobile_get_usage", description: "Data balance — bytes used and available" },
      {
        name: "mobile_list_locations",
        description: "Available cities with speeds, filterable by carrier, country, or state",
      },
      { name: "mobile_list_carriers", description: "Available carriers, filterable by country" },
      { name: "mobile_get_settings", description: "Access point proxy settings" },
      { name: "mobile_list_access_points", description: "List sub-users sharing your data pool" },
      { name: "mobile_create_access_point", description: "Create a new sub-user" },
      {
        name: "mobile_create_ip_whitelist",
        description: "Whitelist IPs with location, carrier, and protocol options",
      },
      { name: "mobile_update_ip_whitelist", description: "Update an existing whitelist entry" },
      {
        name: "mobile_update_settings",
        description: "Update access point routing — city, carrier, protocol",
      },
    ],
  },
  {
    slug: "analytics",
    title: "Analytics",
    subtitle: "3 tools · any active plan",
    tools: [
      {
        name: "get_traffic_analytics",
        description: "Bandwidth and request/failure counts over time, per session or access point",
      },
      {
        name: "get_top_domains",
        description: "Top destination domains ranked by data volume and by request count",
      },
      {
        name: "get_connection_history",
        description:
          "Per-request log — URL, method, status, duration, bytes, source IP, upstream geo",
      },
    ],
  },
  {
    slug: "residential",
    title: "Per GB Residential",
    subtitle: "9 tools · residential_ prefix",
    tools: [
      { name: "residential_get_usage", description: "Residential data balance" },
      {
        name: "residential_list_locations",
        description: "Available locations, filterable by carrier, country, or state",
      },
      {
        name: "residential_list_carriers",
        description: "Available carriers, filterable by country",
      },
      { name: "residential_get_settings", description: "Access point settings" },
      { name: "residential_list_access_points", description: "List residential sub-users" },
      { name: "residential_create_access_point", description: "Create a residential sub-user" },
      {
        name: "residential_create_ip_whitelist",
        description: "Whitelist IPs for residential proxies",
      },
      {
        name: "residential_update_ip_whitelist",
        description: "Update a residential whitelist entry",
      },
      {
        name: "residential_update_settings",
        description: "Update access point routing — city, carrier, protocol",
      },
    ],
  },
];

export interface FeatureCopy {
  slug: string;
  title: string;
  description: string;
}

export const MCP_FEATURES_COPY: FeatureCopy[] = [
  {
    slug: "natural-language",
    title: "Control proxies in plain language",
    description:
      "Rotate IPs, switch cities, check usage, and manage access points by asking your AI agent — no dashboard tab-switching.",
  },
  {
    slug: "smart-discovery",
    title: "Registers only what you can use",
    description:
      "On startup it checks your active Proxidize subscriptions and registers tools for those plans only — nothing you can't call.",
  },
  {
    slug: "any-client",
    title: "Works with any MCP client",
    description:
      "Claude Code, Claude Desktop, Cursor, Windsurf, and VS Code — anything that speaks the Model Context Protocol.",
  },
  {
    slug: "one-command",
    title: "One command to install",
    description:
      "npx -y @proxidize/mcp. No build step, no local checkout, no dependencies to manage.",
  },
  {
    slug: "analytics",
    title: "Built-in usage analytics",
    description:
      "Ask for bandwidth trends, top destination domains, or a full per-request connection history.",
  },
  {
    slug: "open-source",
    title: "Open source, MIT licensed",
    description:
      "Inspect the source, self-host from a local build, or open a pull request on GitHub.",
  },
];

export interface StepCopy {
  title: string;
  description: string;
}

export const MCP_STEPS_COPY: StepCopy[] = [
  {
    title: "Get your API token",
    description:
      "Log in to the Proxidize dashboard, open the avatar dropdown → Settings, and copy your API token.",
  },
  {
    title: "Add the server to your client",
    description:
      "Point Claude Code, Claude Desktop, Cursor, Windsurf, or VS Code at npx -y @proxidize/mcp with your token as an environment variable.",
  },
  {
    title: "Start asking",
    description:
      "Run /mcp in Claude Code to confirm the connection, then rotate IPs, check usage, or manage access points in plain language.",
  },
];

export const MCP_CLAUDE_CODE_CODE = `# Local (default) — current project, private
claude mcp add proxidize -e PROXIDIZE_API_TOKEN=YOUR_API_TOKEN -- npx -y @proxidize/mcp

# User — all your projects, private
claude mcp add proxidize --scope user -e PROXIDIZE_API_TOKEN=YOUR_API_TOKEN -- npx -y @proxidize/mcp

# Project — shared with your team via .mcp.json
claude mcp add --scope project proxidize -- npx -y @proxidize/mcp`;

export const MCP_CLAUDE_DESKTOP_CODE = `{
  "mcpServers": {
    "proxidize": {
      "command": "npx",
      "args": ["-y", "@proxidize/mcp"],
      "env": {
        "PROXIDIZE_API_TOKEN": "YOUR_API_TOKEN"
      }
    }
  }
}`;

export const MCP_OTHER_CLIENTS_CODE = `{
  "mcpServers": {
    "proxidize": {
      "command": "npx",
      "args": ["-y", "@proxidize/mcp"],
      "env": {
        "PROXIDIZE_API_TOKEN": "YOUR_API_TOKEN"
      }
    }
  }
}`;

export interface EnvVarCopy {
  name: string;
  required: boolean;
  defaultValue?: string;
  description: string;
}

export const MCP_ENV_VARS_COPY: EnvVarCopy[] = [
  { name: "PROXIDIZE_API_TOKEN", required: true, description: "Your Proxidize API token." },
  {
    name: "PROXIDIZE_BASE_URL",
    required: false,
    defaultValue: "https://api.proxidize.com/api/v1",
    description: "API base URL.",
  },
  {
    name: "PROXIDIZE_PROXY_HOST",
    required: false,
    defaultValue: "pg.proxi.es",
    description: "Proxy gateway host used in connection URLs.",
  },
  {
    name: "PROXIDIZE_TIMEOUT",
    required: false,
    defaultValue: "30000",
    description: "Request timeout in milliseconds.",
  },
  {
    name: "PROXIDIZE_MAX_RETRIES",
    required: false,
    defaultValue: "2",
    description: "Max retries on server errors (0-5).",
  },
  {
    name: "PROXIDIZE_DISABLE_ANALYTICS",
    required: false,
    description: "Set to 1 to disable anonymous usage analytics (DO_NOT_TRACK=1 also works).",
  },
];

export const MCP_SUPPORTED_CLIENTS = [
  "Claude Code",
  "Claude Desktop",
  "Cursor",
  "Windsurf",
  "VS Code",
];

export interface McpFaqCopy {
  q: string;
  a: string;
  link?: { label: string; href: string };
}

export const MCP_FAQS: McpFaqCopy[] = [
  {
    q: "What is the Proxidize MCP server?",
    a: "It's an MCP (Model Context Protocol) server that gives AI agents like Claude direct access to your Proxidize account — rotating IPs, switching locations, checking usage, and managing access points through natural language instead of the dashboard.",
  },
  {
    q: "What is MCP (Model Context Protocol)?",
    a: "MCP is an open standard that lets AI agents like Claude connect to external tools and data sources — like the Proxidize API — through a common protocol instead of a custom integration per app.",
    link: {
      label: "Read: What is MCP and how does Model Context Protocol work?",
      href: "/blog/what-is-mcp-and-how-does-model-context-protocol-work/",
    },
  },
  {
    q: "Is it free to use?",
    a: "Yes. The MCP server itself is free and open source under the MIT license. You need an active Proxidize subscription for the proxy-management tools to do anything, since they act on your account.",
  },
  {
    q: "Which AI tools does it work with?",
    a: "Any MCP-compatible client, including Claude Code, Claude Desktop, Cursor, Windsurf, and VS Code with the MCP extension.",
  },
  {
    q: "Do I need to install anything?",
    a: "No build step. Point your client at npx -y @proxidize/mcp and Node.js 18+ downloads and runs it on demand.",
  },
  {
    q: "Why do I only see get_subscription and get_outbound_ip?",
    a: "Those two tools always load. Every other tool is registered based on your account's active subscriptions — no Per Proxy plan means no Per Proxy tools, for example. Regenerate your API token from the dashboard if tool discovery isn't finding an active plan.",
  },
  {
    q: "Does it send my data anywhere?",
    a: "The server collects anonymous usage analytics (which tools are called, latency, error rates) via PostHog to help improve it. Tool responses and parameter values are never sent, and sensitive values are redacted automatically. Set PROXIDIZE_DISABLE_ANALYTICS=1 or DO_NOT_TRACK=1 to opt out.",
  },
  {
    q: "Can I run it from source instead of npm?",
    a: "Yes. Clone the GitHub repo, run npm install && npm run build, then point your MCP client at the build output instead of npx.",
  },
  {
    q: "Is there a full setup guide?",
    a: "Yes. The Proxidize Help Center has a complete walkthrough covering installation, configuration, and troubleshooting for every supported client.",
    link: { label: "Proxidize MCP Server documentation", href: DOCS.mcpServer },
  },
  {
    q: "Is the Proxidize MCP server listed anywhere else?",
    a: "Yes. Beyond GitHub and npm, it's also listed on MCP Servers, a community directory of Model Context Protocol servers.",
    link: { label: "View the listing on MCP Servers", href: MCP_SERVERS_DIRECTORY_URL },
  },
];
